import React, { useState } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';

import { EMAIL_REGEX } from '../../../../constants/regex';
import { Typography } from '../../../atoms/typography/Typography';
import { LinkButton } from '../../../atoms/linkButton/LinkButton';
import { Input } from '../../../atoms/input/Input';
import { Stack } from '../../../atoms/stack/Stack';
import { Row } from '../../../atoms/row/Row';
import PrivacyCheckbox from '../../../molecules/shared/privacyCheckbox/PrivacyCheckbox';
import RadioQuestion from '../../../molecules/shared/radioQuestion/RadioQuestion';
import LinkedInLogin from '../../../molecules/linkedIn/linkedInLogin/LinkedInLogin';
import useLinkedIn from '../../../../hooks/useLinkedIn';
import { Loader } from '../../../atoms/loader/Loader';

export const ReviewFormContent = ({
  systemName,
  questions,
  onClose,
  handleSubmit,
  loading,
  saved,
  urls,
  t,
}) => {
  const [submitTouched, setSubmitTouched] = useState(false);
  const [linkedInRedirect, setLinkedInRedirect] = useState(false);
  const { oAuth2Login } = useLinkedIn();

  const methods = useForm({
    defaultValues: {
      privacyTerms: false,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const getSubmitDisabled = () => {
    if (!submitTouched) {
      return false;
    }

    return Object.keys(methods.formState.errors).length > 0 || loading;
  };

  const disabledSubmit = getSubmitDisabled();

  const handleFormSubmitAttempt = async (isLinkedIn = false) => {
    setSubmitTouched(true);

    // This will trigger validation for all fields
    const isValid = await methods.trigger(null, {
      shouldFocus: true,
    });

    if (!isValid) {
      return false;
    }

    methods.handleSubmit(
      async (data) => {
        const dataToSubmit = { ...data };
        delete dataToSubmit.privacyTerms;

        // Prepare
        if (isLinkedIn) {
          setLinkedInRedirect(true);
        }

        // Execute
        const reviewInstanceId = await handleSubmit(dataToSubmit, isLinkedIn);

        // Done
        if (isLinkedIn) {
          oAuth2Login(reviewInstanceId);
        }
      },
      (errors) => {
        console.log(errors);
      }
    )();
  };

  const renderAnswers = (question, includeTypeInKey) => {
    const fieldId = includeTypeInKey
      ? `${question.type}-${question.id}`
      : question.id;

    const questionRequired = question.required;

    switch (question.type) {
      case 'INPUT':
        return (
          <Input
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...methods.register(fieldId, {
              required: questionRequired,
              pattern: {
                value:
                  question.pattern === 'email' && EMAIL_REGEX
                    ? EMAIL_REGEX
                    : null,
              },
            })}
            error={methods.formState.errors[fieldId]}
          />
        );

      case 'TEXTAREA':
        return (
          <Input
            multiline
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...methods.register(fieldId, {
              required: question.required,
            })}
            error={methods.formState.errors[fieldId]}
          />
        );

      case 'RATING':
        return (
          <RadioQuestion
            formName={fieldId}
            answers={[
              {
                value: 1,
                text: '1',
              },
              {
                value: 2,
                text: '2',
              },
              {
                value: 3,
                text: '3',
              },
              {
                value: 4,
                text: '4',
              },
              {
                value: 5,
                text: '5',
              },
            ]}
            required={questionRequired}
            error={methods.formState.errors[fieldId]}
            horizontal
            clearVisible={!questionRequired}
          />
        );

      case 'RADIO': {
        const alternatives = question.reviewAnswers.map((answer) => ({
          text: answer.text,
          value: answer.id,
        }));

        return (
          <RadioQuestion
            formName={fieldId}
            answers={alternatives}
            required={questionRequired}
            error={methods.formState.errors[fieldId]}
            clearVisible={!questionRequired}
          />
        );
      }

      case 'TILES': {
        const alternatives = question.reviewAnswers.map((answer) => ({
          text: answer.text,
          value: answer.id,
        }));

        return (
          <div className="flex gap-3 flex-col md:flex-row">
            <Controller
              control={methods.control}
              name={fieldId}
              rules={{ required: question.required }}
              render={({ field }) =>
                alternatives.map((answer) => (
                  <Row
                    key={answer.value}
                    onClick={() => field.onChange(answer.value)}
                    className={`w-full p-3 ${
                      field.value === answer.value
                        ? 'bg-primary-700'
                        : 'bg-[#EAF5FB]'
                    } ${
                      methods.formState.errors[fieldId] && 'ring-1 ring-red-500'
                    } rounded-md gap-3 global-hover items-center justify-center`}
                  >
                    <Typography
                      fontVariant="sm"
                      textAlign="center"
                      className={`font-medium text-balance ${
                        field.value === answer.value
                          ? '!text-white'
                          : '!text-customGray-900'
                      }`}
                    >
                      {answer.text}
                    </Typography>
                  </Row>
                ))
              }
            />
          </div>
        );
      }

      default:
        break;
    }
    return null;
  };

  const renderQuestion = (question, labelDataToReplace, includeTypeInKey) => {
    let labelText = question.text;

    if (labelDataToReplace) {
      Object.entries(labelDataToReplace).forEach(([key, value]) => {
        labelText = labelText.replaceAll(`{{${key}}}`, value);
      });
    }

    return (
      <label
        key={question.id}
        htmlFor={question.text}
        className="flex flex-col gap-1"
      >
        <Typography
          as="span"
          fontVariant="sm"
          textAlign="left"
          className="font-medium"
        >
          {labelText}{' '}
          <span className={`${!question.required && 'hidden'} text-red-500`}>
            *
          </span>
        </Typography>

        {renderAnswers(question, includeTypeInKey)}
      </label>
    );
  };

  if (saved) {
    return (
      <div className="flex w-full flex-col flex-1 px-8 mx-auto mt-20 mb-10 max-w-3xl">
        <div className="flex flex-col flex-1 justify-center">
          {!linkedInRedirect && (
            <>
              <Typography as="h4" fontVariant="h4">
                {t('formAddReview.doneThanksTitle')}
              </Typography>
              <Typography fontVariant="md" className="font-medium my-5">
                {t('formAddReview.doneThanksText')}
              </Typography>
            </>
          )}

          {linkedInRedirect && (
            <div className="flex flex-col flex-1 items-center justify-center">
              <Typography fontVariant="md" className="font-medium my-5">
                {t('formAddReview.linkedIn.redirect.message')}
              </Typography>
              <Loader />
            </div>
          )}

          {onClose && !linkedInRedirect && (
            <div className="flex flex-col flex-1 items-center justify-center">
              <LinkButton onClick={onClose} textTransform="uppercase">
                {t('common.close')}
              </LinkButton>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto flex w-full h-full flex-col flex-1 px-8 mx-auto mt-20 mb-10 max-w-3xl">
      <div className="flex flex-col flex-1 justify-center">
        <Typography as="h4" fontVariant="h4">
          {t('sp.writeReview')}
        </Typography>
        <Typography fontVariant="md" className="font-medium">
          {t('sp.writeReviewSubtitle', { systemName })}
        </Typography>
        <Typography fontVariant="sm" className="my-5 italic">
          {t('formAddReview.yourNameIsAnonymously')}
        </Typography>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <FormProvider {...methods}>
          <div className="flex flex-col">
            <Stack className="gap-5">
              {questions.map((question) =>
                renderQuestion(question, { systemName }, true)
              )}

              {/* Add review title field */}
              {renderQuestion({
                id: 'title',
                type: 'INPUT',
                text: t('formAddReview.titleForReview'),
              })}

              {/* Add review email field */}
              {renderQuestion({
                id: 'email',
                type: 'INPUT',
                pattern: 'email',
                required: true,
                text: t('formAddReview.yourEmail'),
              })}
            </Stack>
            <PrivacyCheckbox
              privacyLink={urls.legalPrivacyPageUrl}
              termsLink={urls.legalTermsPageUrl}
              className="mt-5"
              required
              t={t}
              {...methods.register('privacyTerms', {
                required: true,
              })}
              error={methods.formState.errors.privacyTerms}
              checked={methods.getValues('privacyTerms')}
              onClick={() => {
                methods.setValue(
                  'privacyTerms',
                  !methods.getValues('privacyTerms'),
                  {
                    shouldValidate: true,
                  }
                );
              }}
            />
            <div className="flex flex-col min-w-90  items-center gap-2 mt-5 md:mt-0 justify-center pt-5">
              <LinkButton
                variant="primary-filled"
                textTransform="uppercase"
                size="lg"
                type="submit"
                className="text-clamp-sm leading-5 border-0 gap-1 border-primary-700 min-w-60 max-md:w-full justify-center"
                onClick={() => handleFormSubmitAttempt(false)}
                clickable={!disabledSubmit}
              >
                {t('formCommon.verifyWithEmail')}
              </LinkButton>
              <LinkedInLogin
                onClick={() => handleFormSubmitAttempt(true)}
                buttonText={t('formCommon.verifyWithLinkedIn')}
                disabled={disabledSubmit}
                className="text-clamp-sm leading-5 border-0 gap-1 min-w-60 max-md:w-full justify-center"
              />
            </div>
            <Typography
              fontVariant="sm"
              as="span"
              className="w-fit mt-5 !text-customGray-500 self-center"
            >
              {t('formCommon.asteriskMeansMandatory')}
            </Typography>
          </div>
        </FormProvider>
      </div>
    </div>
  );
};
